import { ButtonHTMLAttributes, FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import {
  DraftConfigurationColor,
  DraftConfigurationSpecialTextStyle,
  DraftConfigurationTextStyle,
  DraftConfigurationWithTextOrSpecialText,
} from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { assertDefined } from '@/utils/assertDefined';
import { t } from '@/utils/i18n';
import { isMultiLine } from '@/utils/text/isMultiLine';
import {
  isDraftConfigurationWithText,
  isDraftSpecialTextConfiguration,
  isDraftTextConfiguration,
} from '@/model/configuration/isDraftConfigurationType';
import { isStoreFont } from '@/model/font/isFontType';
import { getFonts } from '@/redux/core/getters/getFonts';
import { PrintColor } from '@/components/common/PrintColor';
import { AlignCenter } from '@/components/common/icons/AlignCenter';
import { AlignJustify } from '@/components/common/icons/AlignJustify';
import { AlignLeft } from '@/components/common/icons/AlignLeft';
import { AlignRight } from '@/components/common/icons/AlignRight';
import { Bold } from '@/components/common/icons/Bold';
import { Italic } from '@/components/common/icons/Italic';
import './TextControls.scss';

const ControlButton: FunctionComponent<
  ButtonHTMLAttributes<HTMLButtonElement> & {
    active: boolean;
    testId?: string;
  }
> = ({ title, active, disabled, onClick, testId, children }) => (
  // aria-selected was recommended by usability agency
  // eslint-disable-next-line jsx-a11y/role-supports-aria-props
  <button
    type="button"
    title={title}
    className={cn('text-controls__button', { 'text-controls__button--active': active })}
    aria-selected={active}
    disabled={disabled}
    aria-disabled={disabled}
    onClick={onClick}
    data-testid={testId}
  >
    {children}
  </button>
);

interface Props {
  configuration: DraftConfigurationWithTextOrSpecialText;
  onColorClick: () => void;
  onStyleChange: (style: DraftConfigurationTextStyle | DraftConfigurationSpecialTextStyle) => void;
}

export const TextControls: FunctionComponent<Props> = ({
  configuration,
  onColorClick,
  onStyleChange,
}) => {
  const fonts = useSelector(getFonts);

  const { content, style } = configuration.text;
  const font = style.font;

  let toggleBold: (() => void) | undefined;
  let toggleItalic: (() => void) | undefined;
  let hasFixedPrintColor: boolean;
  let printColor: DraftConfigurationColor;

  if (isDraftConfigurationWithText(configuration)) {
    const style = configuration.text.style;
    const currentFont = style.font;
    const currentFontFamily = assertDefined(
      fonts.find((fontFamily) => fontFamily.id === currentFont.familyId)
    );

    const toggleWeight = currentFont.weight === 'bold' ? 'normal' : 'bold';
    const toggleFontWeight = currentFontFamily.fonts.find(
      (font) => font.weight === toggleWeight && font.style === currentFont.style
    );
    if (toggleFontWeight) {
      toggleBold = () => {
        trackFeature('text-bold');
        onStyleChange({ ...style, font: toggleFontWeight });
      };
    }

    const toggleStyle = currentFont.style === 'italic' ? 'normal' : 'italic';
    const toggleFontStyle = currentFontFamily.fonts.find(
      (font) => font.weight === currentFont.weight && font.style === toggleStyle
    );
    if (toggleFontStyle) {
      toggleItalic = () => {
        trackFeature('text-italic');
        onStyleChange({ ...style, font: toggleFontStyle });
      };
    }

    hasFixedPrintColor = false;
    printColor = configuration.printColors[0];
  } else {
    hasFixedPrintColor = true;
    printColor = { fill: '#FFFFFF' };
  }

  const hasAlign =
    isMultiLine(content) &&
    (isDraftTextConfiguration(configuration) || isDraftSpecialTextConfiguration(configuration));

  const setLeftAlign = () => {
    trackFeature('text-alignment');
    onStyleChange({ ...style, textAlign: 'left' });
  };

  function setCenterAlign() {
    trackFeature('text-alignment');
    onStyleChange({ ...style, textAlign: 'center' });
  }

  function setRightAlign() {
    trackFeature('text-alignment');
    onStyleChange({ ...style, textAlign: 'right' });
  }

  function setJustifyAlign() {
    trackFeature('text-alignment');
    onStyleChange({ ...(style as DraftConfigurationSpecialTextStyle), textAlign: 'justify' });
  }

  return (
    <div className="text-controls">
      <button
        type="button"
        className="text-controls__button text-controls__color"
        onClick={onColorClick}
        disabled={hasFixedPrintColor || !configuration.printColors.length}
      >
        <PrintColor
          printType={configuration.printType}
          color={printColor}
          size={20}
          disabled={hasFixedPrintColor}
        />
        {t('productTypePanel.appearance')}
      </button>
      <ControlButton
        title={t('textSettings.bold')}
        active={isStoreFont(font) && font.weight === 'bold'}
        disabled={!toggleBold}
        onClick={toggleBold}
        testId="toggle-bold"
      >
        <Bold />
      </ControlButton>
      <ControlButton
        title={t('textSettings.italic')}
        active={isStoreFont(font) && font.style === 'italic'}
        disabled={!toggleItalic}
        onClick={toggleItalic}
        testId="toggle-italic"
      >
        <Italic />
      </ControlButton>
      <ControlButton
        title={t('textSettings.alignLeft')}
        active={hasAlign && style.textAlign === 'left'}
        disabled={!hasAlign}
        onClick={setLeftAlign}
        testId="toggle-align-left"
      >
        <AlignLeft />
      </ControlButton>
      <ControlButton
        title={t('textSettings.alignCenter')}
        active={hasAlign && style.textAlign === 'center'}
        disabled={!hasAlign}
        onClick={setCenterAlign}
        testId="toggle-align-center"
      >
        <AlignCenter />
      </ControlButton>
      <ControlButton
        title={t('textSettings.alignRight')}
        active={hasAlign && style.textAlign === 'right'}
        disabled={!hasAlign}
        onClick={setRightAlign}
        testId="toggle-align-right"
      >
        <AlignRight />
      </ControlButton>
      <ControlButton
        title={t('textSettings.alignJustify')}
        active={hasAlign && style.textAlign === 'justify'}
        disabled={!hasAlign || !isDraftSpecialTextConfiguration(configuration)}
        onClick={setJustifyAlign}
        testId="toggle-align-justify"
      >
        <AlignJustify />
      </ControlButton>
    </div>
  );
};
